import { createContext, useContext } from 'react'

/**
 * Themes the app ships — one today. Adding another takes a new entry here,
 * a `[data-theme="<name>"]` block in styles/tokens.css and its browser
 * chrome color in ThemeProvider; the provider already applies any of them
 * to `<html data-theme>` and persists the pick.
 */
export const THEMES = ['dark'] as const
export type Theme = (typeof THEMES)[number]
export const DEFAULT_THEME: Theme = 'dark'

export function isTheme(value: unknown): value is Theme {
  return typeof value === 'string' && (THEMES as readonly string[]).includes(value)
}

export interface ThemeContextValue {
  /** Theme applied to `<html data-theme>`. */
  theme: Theme
  /** Switch theme (persists in localStorage). Nothing calls it while there is one theme. */
  setTheme: (next: Theme) => void
}

export const ThemeContext = createContext<ThemeContextValue | null>(null)

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>')
  return ctx
}
